"use client"

import type { User } from "../types"
import type React from "react"
import { createContext, useState, useContext, useEffect } from "react"
import api from "../utils/api"
import { getProfile } from "../utils/api"; // adjust the path if needed


// Remove the User interface definition since we're now importing it

// Define the AuthContextType interface
interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  loading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<void>
  register: (userData: any) => Promise<void>
  logout: () => void
  clearError: () => void
  refreshUser: () => void
}

// Create the AuthContext
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Create the AuthProvider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Load user from local storage
  useEffect(() => {
    const loadUser = async () => {
      try {
        const token = localStorage.getItem("token")
        if (!token) {
          setLoading(false)
          return
        }

        const response = await api.get("/auth/me")
        setUser(response.data)
        setIsAuthenticated(true)
      } catch (err) {
        localStorage.removeItem("token")
        setUser(null)
        setIsAuthenticated(false)
        setError("Session expired. Please login again.")
      } finally {
        setLoading(false)
      }
    }

    loadUser()
  }, [])

  const refreshUser = async () => {
    try {
      const response = await getProfile();
      setUser(response.data);
    } catch (err) {
      console.error("Failed to refresh user", err);
    }
  };
  

  // Login function
  const login = async (email: string, password: string) => {
    try {
      setLoading(true)
      setError(null)

      const response = await api.post("/auth/login", { email, password })

      localStorage.setItem("token", response.data.access_token)
      setUser(response.data.user)
      setIsAuthenticated(true)
    } catch (err: any) {
      setError(err.response?.data?.error || "Login failed. Please try again.")
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Register function
  const register = async (userData: any) => {
    try {
      setLoading(true)
      setError(null)

      const response = await api.post("/auth/register", userData)

      localStorage.setItem("token", response.data.access_token)
      setUser(response.data.user)
      setIsAuthenticated(true)
    } catch (err: any) {
      setError(err.response?.data?.error || "Registration failed. Please try again.")
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Logout function
  const logout = () => {
    localStorage.removeItem("token")
    setUser(null)
    setIsAuthenticated(false)
  }

  // Clear error function
  const clearError = () => {
    setError(null)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        error,
        login,
        register,
        logout,
        clearError,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// Create a hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
